import {
  Badge,
  Box,
  Checkbox,
  Chip,
  FormControl,
  FormGroup,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Radio,
  Select,
  Typography,
} from '@mui/material';
import { Amplify } from 'aws-amplify';
import { generateClient, GraphQLSubscription } from 'aws-amplify/api';
import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { SlackService } from '../../../../service/slack.service';
import zeplinColor from '../../../../theme/Color';
import CardStack from '../../CardStack/CardStack';
import CustomButton from '../../CustomButton/CustomButton';
import CustomPopOver from '../../CustomPopOver/CustomPopOver';
import { LightTooltip } from '../../CustomTooltip/CustomTooltip';
import InfiniteScrollColumn from '../../InfiniteScrollColumn/InfiniteScrollColumn';
import style from './ActivityTab.module.css';
import CustomActivityTimeline from './CustomActivityTimeline';
import SlackMessageCard from './CustomSlackTimeline';
// Make sure the aws-exports file exists at the correct path.
// If it does not exist, run `amplify pull` or `amplify configure` in your project root to generate it.
import config from '../../../../model/aws-exports';
// If the correct path is different, update accordingly.
// Update the import path to the correct location, for example:
import { onSlackEvent } from '../../../../model/graphql/subscriptions';
import { HuddleClientService } from '../../../../service/huddle-client.service';
import { HuddleDashboardService } from '../../../../service/huddle-dashboard.service';
import { Utilities } from '../../../../utilities/utilities';
import { renderSlackMessage } from '../../../featured/Client/ClientSelected/CommunicationTab/helper/renderSlackMessage';
// If the file does not exist, create 'subscriptions.ts' in 'src/model/graphql/' and export 'onSlackEvent' from there.

Amplify.configure(config as any);
const client = generateClient();

// const CHANNEL_ID = 'general';
// Slack CHANNEL IDs (the C0... strings) — see ActivityColumn.tsx comment for how to find them.
const CHANNEL_IDS = [
  'C0971NJP4F9', // huddle-grid-testers
  // TODO: add other channels by ID, not name
];
const MenuProps = {
  PaperProps: {
    sx: {
      borderRadius: '14px',
    },
  },
};

const ActivityTab = () => {
  const location = useLocation();
  const code = location?.search?.split('code=')[1];
  const IsSlackConnected = sessionStorage.getItem('isSlackConnected') === 'true';
  const IsSlackConnecting = sessionStorage.getItem('slackConnecting') === 'true';
  const params = useParams();
  const [loading, setLoading] = useState(true);
  const [anchorEl, setAnchorEl] = React.useState<HTMLDivElement | null>(null);
  const [applied, setApplied] = useState(false);
  const [slackChannel, setSlackChannel] = useState<string | null>(null);
  const [selectedItems, setSelectedItems] = useState<{
    time: string[];
    tools: string[];
  }>({
    time: [],
    tools: [],
  });

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;
  const Tools = ['Huddle', 'Slack'];

  const TimeRange = ['Day', 'Week', 'Month'];

  const handleClearAll = () => {
    setSelectedItems({
      time: [],
      tools: [],
    });
    // getTodayActivity();
    setAnchorEl(null);
    getTodayActivity(false, true);
    setApplied(false);
  };

  const handleSelection = (item: any, type: string) => {
    const key = type as keyof typeof selectedItems;

    const {
      target: { value },
    } = item;

    setSelectedItems({
      ...selectedItems,
      [key]: typeof value === 'string' ? value.split(',') : value,
    });
  };

  const [todayLoading, setTodayLoading] = useState(true);

  const [todayData, setTodayData] = useState<{
    records: any[];
    totalSize: number;
    done: boolean;
    totalFilteredRecords: 0;
  }>({
    records: [],
    totalSize: 0,
    done: true,
    totalFilteredRecords: 0,
  });

  const [todayOffset, setTodayOffset] = useState(0);
  const [newDataTotal, setNewDataTotal] = useState(0);
  const [showSlackConnect, setShowSlackConnect] = useState(true);
  const [events, setEvents] = useState<any[]>([]);
  const [slackMessage, setSlackMessage] = useState<any[]>([]);
  // const [nextSlack, setNextSlack] = useState<string | null>(null);
  const nextSlackRef = useRef<string | null>(null);
  const slackMessageRef = useRef(slackMessage);
  const [slackLoading, setSlackLoading] = useState(false);
  const [channelCursors, setChannelCursors] = useState<Record<string, string | null>>({});
  const [channelLoading, setChannelLoading] = useState<Record<string, boolean>>({});
  const [channelID, setChannelID] = useState<string | null>(null);

  const normalizeSlackMessage = (message: any) => ({
    ...message,
    text: renderSlackMessage(message),
    replies: Array.isArray(message?.replies)
      ? message.replies.map((reply: any) => ({
          ...reply,
          text: renderSlackMessage(reply),
        }))
      : message?.replies,
  });

  const toSlackEventPayload = (event: any) =>
    normalizeSlackMessage({
      ...event,
      userDetails: { name: event?.user, image: event?.userImage },
    });

  useEffect(() => {
    slackMessageRef.current = slackMessage;
  }, [slackMessage]);
  useEffect(() => {
    // Create the subscription
    console.log('Setting up SlackFeed subscription, IsSlackConnected:', IsSlackConnected);
    const subscription = IsSlackConnected
      ? CHANNEL_IDS.map((id) =>
          client
            .graphql<GraphQLSubscription<typeof onSlackEvent>>({
              query: onSlackEvent,
              variables: { channelId: id },
              authMode: 'apiKey' as const,
            })
            .subscribe({
              next: (response) => {
                // The response type is GraphqlSubscriptionMessage<GraphQLSubscription<typeof onSlackEvent>>
                const event = (response.data as any)?.onSlackEvent;
                if (event) {
                  console.log('Received event:', event);
                  const match = slackMessageRef.current?.find((msg) =>
                    msg?.thread_ts
                      ? msg?.thread_ts === event?.thread_ts
                      : msg?.ts === event?.thread_ts,
                  );

                  if (event?.parentText && event?.parentTS && event?.eventType === 'message') {
                    // We're dealing with a reply in a thread
                    // Find the parent message and update the specific reply
                    const newThread = {
                      ...event,
                      text: renderSlackMessage({ text: event?.parentText }), // Use parent text for the parent message
                      // ts: event?.ts, // Use parent timestamp
                      ts: event?.parentTS ? event?.parentTS : event?.ts, // Use parent timestamp
                      ...(event?.parentTS && {
                        replies: [
                          {
                            text: renderSlackMessage(event), // Update the reply text
                            ts: event?.ts, // Update the reply timestamp

                            userDetails: {
                              name: event?.user,
                              image: event?.userImage,
                            },
                          },
                        ],
                      }),
                      userDetails: {
                        name: event?.parentUserName,
                        image: event?.parentUserImage,
                      },
                    };
                    // Update events array
                    setEvents((prev) => [
                      ...prev.filter((msg) => msg?.ts !== event?.parentTS),
                      newThread,
                    ]);

                    // Move the updated parent thread to the top of the list
                    setSlackMessage((prev) => [
                      ...(slackMessageRef.current.filter((msg) =>
                        msg?.thread_ts
                          ? msg?.thread_ts === event?.thread_ts
                          : msg?.ts === event?.thread_ts,
                      )?.length
                        ? slackMessageRef.current
                            ?.filter((msg) =>
                              msg?.thread_ts
                                ? msg?.thread_ts === event?.thread_ts
                                : msg?.ts === event?.thread_ts,
                            )
                            ?.map((msgReturn: any) => ({
                              ...msgReturn,
                              replies: [
                                ...(msgReturn?.replies?.length ? msgReturn?.replies : []),
                                {
                                  text: renderSlackMessage(event),
                                  ts: event?.ts,
                                  userDetails: { name: event?.user, image: event?.userImage },
                                },
                              ],
                            }))
                        : [newThread]),
                      ...slackMessageRef.current?.filter((msg) =>
                        msg?.thread_ts
                          ? msg?.thread_ts !== event?.thread_ts
                          : msg?.ts !== event?.thread_ts,
                      ),
                    ]);
                  } else if (event?.eventType === 'message deleted') {
                    if (event?.thread_ts) {
                      const filterMatch = slackMessageRef.current.filter((msg) =>
                        msg?.thread_ts
                          ? msg?.thread_ts === event?.thread_ts
                          : msg?.ts === event?.thread_ts,
                      );
                      if (filterMatch?.length) {
                        setSlackMessage((prev) => {
                          // Find the thread's current position in the array
                          const threadPositions = prev.map((msg, index) => ({
                            msg,
                            index,
                            isMatch: msg?.thread_ts
                              ? msg?.thread_ts === event?.thread_ts
                              : msg?.ts === event?.thread_ts,
                          }));

                          // Create a new array with the same ordering
                          const newArray = [...prev];

                          // Update each thread that matches
                          threadPositions
                            .filter((item) => item.isMatch)
                            .forEach(({ index }) => {
                              // Replace the thread at its current position with the updated version
                              newArray[index] = {
                                ...prev[index],
                                replies: prev[index]?.replies?.filter(
                                  (reply: any) => reply?.ts !== event?.ts,
                                ),
                              };
                            });

                          return newArray;
                        });
                      }
                    } else {
                      const parsedEventText = renderSlackMessage(event);
                      setSlackMessage((prev) =>
                        prev?.filter((msg) => msg.text !== parsedEventText),
                      );
                      setEvents((prev) => [...prev?.filter((msg) => msg.text !== parsedEventText)]);
                    }
                  } else if (event?.eventType === 'edited message') {
                    if (event?.originalText && event?.text === event?.originalText) {
                      return;
                    }

                    const parsedOriginalText = renderSlackMessage({ text: event?.originalText });

                    if (event?.thread_ts) {
                      // Find all messages in the thread
                      const foundThreadMessages = slackMessageRef.current.filter((msg) =>
                        msg?.thread_ts
                          ? msg?.thread_ts === event?.thread_ts
                          : msg?.ts === event?.thread_ts,
                      );

                      // Map them to updated versions
                      const updatedThreadMessages = foundThreadMessages.map((threadMsg) => {
                        // Case 1: This is the parent message being edited
                        if (threadMsg.text === parsedOriginalText || threadMsg?.ts === event?.ts) {
                          return {
                            ...threadMsg,
                            ...event,
                            text: renderSlackMessage(event),
                            ts: event?.ts,
                            isEdited: true,
                            // Preserve the original replies array
                            replies: threadMsg.replies || [],
                          };
                        }
                        // Case 2: This is a parent with a reply being edited
                        else {
                          return {
                            ...threadMsg,
                            // Map through replies to find and update the edited one
                            replies: threadMsg?.replies?.map((reply: any) =>
                              reply?.text === parsedOriginalText || reply?.ts === event?.ts
                                ? {
                                    ...reply,
                                    ...event,
                                    text: renderSlackMessage(event),
                                    ts: event?.ts,
                                    isEdited: true,
                                  }
                                : reply,
                            ),
                          };
                        }
                      });

                      // Update the events array with the new thread messages
                      setEvents((prev) => [
                        ...updatedThreadMessages,
                        ...prev.filter(
                          (msg) =>
                            msg?.thread_ts !== event?.thread_ts && msg?.ts !== event?.thread_ts,
                        ),
                      ]);

                      // Update the slackMessage array with the new thread messages
                      setSlackMessage((prev) => [
                        ...updatedThreadMessages,
                        ...prev.filter(
                          (msg) =>
                            msg?.thread_ts !== event?.thread_ts && msg?.ts !== event?.thread_ts,
                        ),
                      ]);
                    } else {
                      const foundMsg = slackMessageRef.current
                        .filter(
                          (msgFilter) =>
                            msgFilter?.ts === event?.ts ||
                            msgFilter?.text === renderSlackMessage({ text: event?.originalText }),
                        )
                        .map((msg) => ({
                          ...msg,
                          text: renderSlackMessage(event),
                          ts: event?.ts,
                          isEdited: true,
                        }));
                      if (foundMsg?.length) {
                        setEvents((prev) => [
                          ...prev.filter((msg) => msg?.ts !== event?.ts),
                          ...slackMessageRef.current
                            .filter(
                              (msg) => msg?.ts === event?.ts || msg?.text === parsedOriginalText,
                            )
                            .map((msgReturn: any) => {
                              return {
                                ...msgReturn,
                                text: renderSlackMessage(event),
                                ts: event?.ts,
                              };
                            }),
                        ]);
                        // Update slackMessage state, bringing edited messages to the top
                        setSlackMessage((prev) => [
                          ...foundMsg,
                          ...prev.filter((msg) => msg?.ts !== event?.ts),
                        ]);
                        // This will log the messages that were edited
                        // later add comparison of msg?.ts === event?.originalTs...next to wherever originalText is being compared.
                      } else {
                        setEvents((prev) => [
                          ...prev.filter((msg) => msg?.ts !== event?.ts),
                          {
                            ...event,
                            text: renderSlackMessage(event),
                            ts: event?.ts,

                            userDetails: { name: event?.user, image: event?.userImage },
                          },
                        ]);
                        setSlackMessage((prev) => [
                          toSlackEventPayload(event),
                          ...prev.filter((msg) => msg?.ts !== event?.ts),
                        ]);
                      }
                    }
                  } else {
                    setSlackMessage((prev) => [toSlackEventPayload(event), ...prev]);
                    setEvents((prev) => [toSlackEventPayload(event), ...prev]);
                  }
                  // setEvents((prev: SlackEvent[]) => [event, ...prev]);
                }
              },
              error: (error: Error) => console.error('Subscription error:', error),
            }),
        )
      : undefined;
    console.log({ subscription });

    return () => subscription?.forEach((sub) => sub.unsubscribe());
  }, [IsSlackConnected]);

  const getAllClient = async (refresh = false, tokenInit = false) => {
    setLoading(true);
    try {
      const storedPath = sessionStorage.getItem('redirect_path') || '';
      console.log({ storedPath, location, IsSlackConnecting, IsSlackConnected });
      if ((storedPath && location?.pathname === storedPath) || !storedPath) {
        // const res = location?.pathname?.includes('huddle')
        //   ? await HuddleClientService.getAllClient()
        //   : await ClientService.getAllClient();
        const res = await HuddleClientService.getAllClient();
        // const storedPath = sessionStorage.getItem('redirect_path') || '';
        const currentAccountId = location.pathname.split('/').pop();

        if (currentAccountId && res?.clients) {
          const filteredClient = res.clients.filter(
            (client: any) =>
              client.AccountId === currentAccountId || client.Id === currentAccountId,
          );

          if (IsSlackConnected || tokenInit) {
            refresh && setSlackMessage([]);
            if (filteredClient.length > 0) {
              if (filteredClient[0]?.slackChannelId) {
                setChannelID(filteredClient[0]?.slackChannelId);
                // getSlackMessage(filteredClient[0]?.slackChannelId);
                filteredClient[0]?.slackChannelId?.split(',')?.map((val: string) => {
                  getSlackMessage(val);
                });
              }
            } else if (location?.pathname?.includes('dashboard')) {
              if (process.env.REACT_APP_ENV === 'prod') {
                await getSlackMessage('C01G3ALUCG2');
                await getSlackMessage('C055KKGL09J');
                await getSlackMessage('C088KR2JJJ0');
                await getSlackMessage('C01FX2DETT8');
              } else {
                await getSlackMessage('C0971NJP4F9');
                await getSlackMessage('C09AS1U38L8');
              }
            }
          }
        } else {
          console.log('No AccountId in URL or no clients in response');
        }
        sessionStorage.removeItem('sf_redirect');
        sessionStorage.removeItem('redirect_path');
        sessionStorage.removeItem('slackConnecting');
      }
    } catch (err: any) {
      console.log('Error fetching clients:', err);
    } finally {
      setLoading(false);
    }
  };
  const getTodayActivity = async (next?: boolean, init?: boolean) => {
    setTodayLoading(true);
    try {
      // 1. Prepare Query Params
      const toolsQuery = init ? '' : selectedItems.tools.join(',');
      const timeQuery = init
        ? ''
        : selectedItems.time
            .map((val: string) => (val === 'Day' ? 'today' : 'this' + val))
            .join(',');

      const requestOffset = next ? todayOffset + 10 : 0;

      const commonPayload = {
        limit: 10,
        timeRange: timeQuery,
        tools: toolsQuery,
        offset: requestOffset,
      };

      // 2. Setup Promises
      let primaryRequestPromise;
      let huddleRequestPromise: any = Promise.resolve(null); // Default to no-op

      if (params?.id) {
        // Case A: Specific Client/Team View
        if (location?.pathname?.includes('huddle')) {
          primaryRequestPromise = HuddleClientService.getHuddleActivity(params?.id, commonPayload);
        }
        //  else {
        //   primaryRequestPromise = ClientService.getActivity(params?.id, commonPayload);
        // }
      } else {
        // Case B: Main Dashboard (Fetch Both)
        // primaryRequestPromise = DashboardService.getActivity(commonPayload);
        huddleRequestPromise = HuddleDashboardService.getHuddleActivity(commonPayload);
      }

      // 3. Parallel Execution
      const [resPrimary, resHuddle] = await Promise.all([
        primaryRequestPromise,
        huddleRequestPromise,
      ]);

      // 4. Extract & Tag Data
      const primaryActivityRaw = resPrimary?.lastActivity || resPrimary;
      const huddleActivityRaw = resHuddle?.lastActivity || resHuddle;

      const primaryRecords = (primaryActivityRaw?.records || []).map((record: any) => ({
        ...record,
        source: location?.pathname?.includes('huddle') ? 'HUDDLE' : 'SALESFORCE',
      }));

      const huddleRecords = (huddleActivityRaw?.records || []).map((record: any) => ({
        ...record,
        source: 'HUDDLE',
      }));

      // 5. Merge & Sort
      let finalRecords = [...primaryRecords, ...huddleRecords];

      finalRecords.sort((a, b) => {
        const dateA = a.date ? new Date(a.date).getTime() : 0;
        const dateB = b.date ? new Date(b.date).getTime() : 0;
        const validA = isNaN(dateA) ? 0 : dateA;
        const validB = isNaN(dateB) ? 0 : dateB;
        return validB - validA; // Descending (Newest First)
      });

      // Calculate Merged Total
      let finalTotal = primaryActivityRaw?.totalSize || 0;
      if (huddleActivityRaw) {
        finalTotal += huddleActivityRaw.totalSize || 0;
      }

      // Create Merged Response Object
      const mergedResponse = {
        ...primaryActivityRaw, // Keep base metadata
        totalSize: finalTotal,
        records: finalRecords,
      };

      // 6. Handle Side Effects (New Data Notification)
      if (!init && !timeQuery && !toolsQuery && !next) {
        // Calculate difference based on the new combined total
        setNewDataTotal(mergedResponse.totalSize - (todayData?.totalSize || 0));
      }

      // 7. Update Offset
      if (next) {
        setTodayOffset((prev) => prev + 10);
      } else {
        setTodayOffset(0);
      }

      // 8. Update State
      if (mergedResponse) {
        if (!next) {
          // Initial Load / Refresh
          setTodayData(mergedResponse);
        } else {
          // Pagination: Append new merged records to existing state
          setTodayData((prev) => ({
            ...mergedResponse, // Updates totalSize
            records: [...prev.records, ...mergedResponse.records],
          }));
        }
      }
    } catch (err) {
      console.log(err);
    } finally {
      setTodayLoading(false);
      setLoading(false);
    }
  };
  const getSlackMessage = async (channelId: string, next?: string) => {
    if (slackLoading && next) {
      return;
    }

    try {
      setChannelLoading((prev) => ({ ...prev, [channelId]: true }));
      setSlackLoading(true);
      setTodayLoading(true);
      const res = await SlackService.getSlackChannelMessages(channelId, next);
      console.log({ reset: res });
      setSlackChannel(res?.channelName);
      setChannelCursors((prev) => ({
        ...prev,
        [channelId]: res?.next_cursor || null,
      }));
      // setSlackMessage((prev) => [...prev, ...(res?.messages || [])]);
      const filteredMessages = (res?.messages || []).filter((message: any) => {
        // Remove messages from USLACKBOT
        return message?.user !== 'USLACKBOT' && message?.sender !== 'USLACKBOT';
      });

      const messagesWithChannel =
        filteredMessages?.map((message: any) => ({
          ...normalizeSlackMessage(message),
          channelId: channelId, // Store the channel ID
          channel: res?.channelName, // Store the channel name
        })) || [];
      setSlackMessage((prev) => [...prev, ...messagesWithChannel]);

      // setSlackMessage((prev) => [...prev, ...filteredMessages]);
      // setNextSlack(res?.next_cursor || null);
      nextSlackRef.current = res?.next_cursor || null;
    } catch (err) {
      console.error('Error fetching Slack message:', err);
    } finally {
      setChannelLoading((prev) => ({ ...prev, [channelId]: false }));
      setSlackLoading(false);
      setTodayLoading(false);
    }
  };

  const getSlackToken = async () => {
    try {
      if (code) {
        setLoading(true);
        const res = await SlackService.getSlackLogin(code.replace('&state=', ''));
        console.log({ res });
        if (res?.ok) {
          window.dispatchEvent(
            new CustomEvent('sessionStorageChange', {
              detail: { key: 'isSlackConnected', value: 'true' },
            }),
          );
          setShowSlackConnect(false);
          sessionStorage.setItem('isSlackConnected', 'true');
          sessionStorage.removeItem('slackConnecting');
          // await getAllClient(false, true);
        }
      }
    } catch (err) {
      console.error('Error fetching Slack token:', err);
    }
  };
  useEffect(() => {
    const initializeData = async () => {
      setLoading(true);

      if (IsSlackConnecting) {
        await getSlackToken();
      }

      console.log({ isSlackConnected: IsSlackConnected, code, IsSlackConnecting });
      IsSlackConnected && getAllClient(true, true);
      await getTodayActivity(false, true);
      sessionStorage.removeItem('dealSelectedFromOverview');
      setLoading(false);
    };

    initializeData();
  }, [IsSlackConnected]);

  const getTodayAndYesterday = () => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);

    return {
      today: today.toLocaleDateString(),
      yesterday: yesterday.toLocaleDateString(),
    };
  };

  const { today, yesterday } = getTodayAndYesterday();

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMarkRead = () => {
    setNewDataTotal(0);
    setEvents([]);
  };
  const applyFilter = () => {
    getTodayActivity();
    setAnchorEl(null);
    setApplied(true);
  };
  const getNextChannelWithCursor = (): { channelId: string; cursor: string } | null => {
    const availableChannels = Object.entries(channelCursors).filter(
      ([_, cursor]) => cursor !== null,
    );

    if (availableChannels.length === 0) return null;

    // Return the first channel with a cursor (you could implement more sophisticated logic here)
    const [channelId, cursor] = availableChannels[0];
    return { channelId, cursor: cursor! };
  };

  const renderActivity = (
    val: any,
    index: number,
    group: 'today' | 'yesterday' | 'otherDate' = 'today',
  ) => {
    if (val?.type === 'message') {
      // Skip individual rendering as we'll handle them in groups

      return null;
    }
    return (
      <>
        <CustomActivityTimeline
          val={val}
          key={index}
          loading={todayLoading}
          newDataTotal={newDataTotal}
          index={index}
        />

        {/* Activities from Other Dates */}
      </>
    );
  };
  const groupSlackMessagesBySender = (type: string) => {
    const filteredMessages =
      type === 'today' || type == 'yesterday'
        ? groupedActivities[type]?.filter((item: any) => item.type === 'message')
        : groupedActivities.otherDates[type]?.filter((item: any) => item.type === 'message');
    return (
      <Box sx={{}}>
        {todayLoading &&
          slackMessage?.length &&
          [1, 2, 3, 4, 5].map((val: any, ind: number) => (
            <CustomActivityTimeline
              val={val}
              key={ind}
              loading={true}
              newDataTotal={0}
              index={ind}
            />
          ))}
        <>
          {filteredMessages?.length &&
            filteredMessages?.map((val: any, ind: any) => {
              // Skip subtype messages (like join/leave notifications)
              // if (val?.subtype) return null;

              // Create an array of messages for this thread
              const messagesCopied = [];

              // Add the parent message first
              messagesCopied.push({
                ...val,
                new:
                  events?.filter((event) => event?.ts === val?.ts && event?.text === val?.text)
                    ?.length > 0,
                id: `slack_${val.user}_${val.ts}`,
                channel: val?.channel || slackChannel,
                isSlack: true,
                sender: val?.userDetails?.name,
                sender_img: val?.userDetails?.image,
                account: val?.text,
                date: val.ts ? new Date(parseInt(val.ts.split('.')[0]) * 1000) : new Date(),
                // date: val?.humanReadableTimestamp
                //   ? new Date(val?.humanReadableTimestamp)
                //   : new Date(),
              });

              // Add replies if they exist
              if (val.replies && Array.isArray(val.replies) && val.replies.length > 0) {
                for (let i = 0; i < val?.replies.length; i++) {
                  const reply = val?.replies[i];
                  messagesCopied.push({
                    ...reply,
                    id: `slack_reply_${reply.user}_${reply.ts}`,
                    new:
                      events?.filter((event) => event?.ts === val?.ts && event?.text === val?.text)
                        ?.length > 0,
                    isSlack: true,
                    isReply: true,
                    parentMessage: val.text,
                    sender: reply?.userDetails?.name,
                    sender_img: reply?.userDetails?.image,
                    account: reply.text,
                    date: reply.ts ? new Date(parseInt(reply.ts.split('.')[0]) * 1000) : new Date(),
                    // date: reply?.humanReadableTimestamp
                    //   ? new Date(reply?.humanReadableTimestamp)
                    //   : new Date(),
                  });
                }
              }

              return (
                <Box
                  key={`stack_${val.user}_${val.ts}`}
                  sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '16px',
                    position: 'relative',
                  }}
                >
                  <CardStack
                    height={120}
                    extraHeightToshowmore={32}
                    cards={messagesCopied}
                    isTab={true}
                    // maxHeight={messages.length < 3 ? '120px' : `${messages.length * 40 + 40}px`}
                    renderCard={(card, isExpanded, isLast, isFirst, total, handleCardClick) => (
                      <SlackMessageCard
                        message={card}
                        isExpanded={isExpanded}
                        newDataTotal={newDataTotal}
                        index={ind}
                        isLast={isLast}
                        isFirst={isFirst}
                        total={total}
                        handleCardClick={handleCardClick}
                      />
                    )}
                  />
                </Box>
              );
            })}
        </>

        {/* <ToastContainer stacked /> */}
      </Box>
    );
  };
  // Grouping activities by date
  const groupActivitiesByDate = (records: any[]) => {
    const grouped: any = {
      today: [],
      yesterday: [],
      otherDates: {},
    };

    records.forEach((record: any) => {
      const activityDate = Utilities.formatDate(
        record?.ts ? new Date(parseInt(record?.ts.split('.')[0]) * 1000) : record?.date,
      );
      if (record) {
        if (activityDate === today) {
          grouped.today.push(record);
        } else if (activityDate === yesterday) {
          grouped.yesterday.push(record);
        } else {
          if (!grouped.otherDates[activityDate]) {
            grouped.otherDates[activityDate] = [];
          }
          grouped.otherDates[activityDate].push(record);
        }
      }
    });

    return grouped;
  };

  const groupedActivities = groupActivitiesByDate([...todayData.records, ...slackMessage]);

  const hasFilteredData = (dateCategory: 'today' | 'yesterday' | string) => {
    if (!applied) return true;

    const categoryData =
      dateCategory === 'today' || dateCategory === 'yesterday'
        ? groupedActivities[dateCategory]
        : groupedActivities.otherDates[dateCategory];

    if (!categoryData || categoryData.length === 0) return false;

    // ✅ FIX: If Filter is Applied, but NO TOOLS are selected (Time filter only),
    // we should show the section if it has data.
    if (selectedItems.tools.length === 0) {
      return true;
    }

    const hasToolData = selectedItems.tools.some((tool) => {
      if (tool === 'Huddle') {
        return categoryData.some((item: any) => item.source === 'HUDDLE');
      }
      if (tool === 'Slack') {
        return categoryData.some((item: any) => item.type === 'message');
      }
      return false;
    });

    return hasToolData;
  };
  const children = () => {
    // Helper function to filter Non-Slack items (Salesforce/Huddle)
    const shouldRenderActivity = (item: any) => {
      if (item.type === 'message') return false; // Slack is handled separately
      if (!applied) return true; // No filter = show all
      if (selectedItems.tools.length === 0) return true; // Applied but empty = show all

      // Check specific sources

      if (item.source === 'HUDDLE') return selectedItems.tools.includes('Huddle');

      return true;
    };

    // Helper to check if we should show the Slack block
    // Helper to check if we should show the Slack block
    const shouldShowSlack = () => {
      if (!IsSlackConnected) return false;
      if (!applied) return true;

      if (selectedItems.tools.length === 0) return true;

      return selectedItems.tools.includes('Slack');
    };

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
        }}
      >
        <>
          {/* Slack Login Button Logic */}
          {!IsSlackConnected && showSlackConnect && !location?.pathname?.includes('team') && (
            <CardStack
              height={128}
              extraHeightToshowmore={32}
              cards={[{ id: '', account: '', isSlack: true, sender: '', channel: '' }]}
              isLoginBtn={true}
              isTab={true}
              renderCard={() => (
                <SlackMessageCard
                  message={''}
                  isExpanded={true}
                  newDataTotal={0}
                  index={0}
                  isLast={true}
                  isFirst={true}
                  total={0}
                  handleCardClick={() => {}}
                  isLoginBtn={true}
                  setShowSlackConnect={setShowSlackConnect}
                />
              )}
            />
          )}

          {/* TODAY SECTION */}
          {groupedActivities.today.length > 0 && (!applied || hasFilteredData('today')) && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: { xs: '10px', xl: '16px' },
              }}
            >
              <Typography variant='responsiveHeading4'>Today</Typography>

              {/* Slack Messages Block */}
              {shouldShowSlack() &&
                groupedActivities.today.filter((item: any) => item.type === 'message')?.length >
                  0 && <Box> {groupSlackMessagesBySender('today')}</Box>}

              {/* Activity Block (SF & Huddle) */}
              <Box>
                {groupedActivities.today
                  .filter(shouldRenderActivity) // THIS FILTER FIXES THE ISSUE
                  .map(renderActivity)}
              </Box>
            </Box>
          )}

          {/* YESTERDAY SECTION */}
          {groupedActivities.yesterday.length > 0 && (!applied || hasFilteredData('yesterday')) && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: { xs: '10px', xl: '16px' },
              }}
            >
              <Typography variant='responsiveHeading4'>Yesterday</Typography>

              {/* Slack Messages Block */}
              {shouldShowSlack() &&
                groupedActivities.yesterday.filter((item: any) => item.type === 'message')?.length >
                  0 && <Box> {groupSlackMessagesBySender('yesterday')}</Box>}

              {/* Activity Block (SF & Huddle) */}
              <Box>
                {groupedActivities.yesterday
                  .filter(shouldRenderActivity) // THIS FILTER FIXES THE ISSUE
                  .map(renderActivity)}
              </Box>
            </Box>
          )}

          {/* OTHER DATES SECTION */}
          {Object.keys(groupedActivities.otherDates)
            .sort((a, b) => {
              const dateA = Utilities.parseDDMMYYYY(a);
              const dateB = Utilities.parseDDMMYYYY(b);
              return dateB.getTime() - dateA.getTime();
            })
            ?.filter((dateString) => !applied || hasFilteredData(dateString))
            ?.map((dateString) => (
              <Box
                key={dateString}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: { xs: '10px', xl: '16px' },
                }}
              >
                <Typography variant='responsiveHeading4'>{dateString}</Typography>

                {/* Slack Messages Block */}
                {shouldShowSlack() &&
                  groupedActivities.otherDates[dateString].filter(
                    (item: any) => item.type === 'message',
                  )?.length > 0 && <> {groupSlackMessagesBySender(dateString)}</>}

                {/* Activity Block (SF & Huddle) */}
                <Box>
                  {groupedActivities.otherDates[dateString]
                    .filter(shouldRenderActivity) // THIS FILTER FIXES THE ISSUE
                    .map(renderActivity)}
                </Box>
              </Box>
            ))}
        </>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: { xs: '10px', xl: '16px' },
        // flexGrow: 1,
        // overflowY: 'auto',
        // height: screenHeight > 640 ? '54vh' : screenHeight > 500 ? '41vh' : '39vh',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          display: 'flex',
          gap: { xs: '10px', xl: '16px' },
        }}
      >
        <LightTooltip
          title={
            <Typography variant='responsiveBody3' color={zeplinColor.Neutral1}>
              Refresh
            </Typography>
          }
          placement='bottom-end'
          style={{ backgroundColor: zeplinColor.BasicBlack, borderRadius: '8px' }}
        >
          <Box
            sx={{
              borderRadius: '50px',
              width: { xs: '28px', xl: '48px' },
              height: { xs: '28px', xl: '48px' },
              border: `1px solid ${zeplinColor.Neutral6}`,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              position: 'relative',
              cursor: 'pointer',
            }}
            onClick={async () => {
              getTodayActivity();
              await getAllClient(true);
              setEvents([]);
            }}
          >
            <img
              src={'/assets/refresh.svg'}
              alt='refresh-activity'
              id='refresh-activity'
              className={style.icon}
            />
          </Box>
        </LightTooltip>
        <LightTooltip
          title={
            <Typography variant='responsiveBody3' color={zeplinColor.Neutral1}>
              Mark all as read
            </Typography>
          }
          placement='bottom-end'
          style={{ backgroundColor: zeplinColor.BasicBlack, borderRadius: '8px' }}
          // sx={{ [`& .${tooltipClasses.tooltip}`]: { backgroundColor: zeplinColor.Neutral1 } }}
        >
          <Box
            sx={{
              borderRadius: '50px',
              width: { xs: '28px', xl: '48px' },
              height: { xs: '28px', xl: '48px' },
              border: `1px solid ${zeplinColor.Neutral6}`,
              // p: '0px 10px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              position: 'relative',
              cursor: 'pointer',
            }}
            onClick={handleMarkRead}
          >
            <img
              src={'/assets/done_all.svg'}
              alt='mark-read-activity'
              id='mark-read-activity'
              className={style.icon}
            />
          </Box>
        </LightTooltip>
        <Badge
          badgeContent={Object.values(selectedItems).reduce(
            (prev, val) => (val?.length ? prev + 1 : prev),
            0,
          )}
          color='primary'
          sx={{
            '.MuiBadge-badge': {
              top: { xs: '0.5', xl: '0.7rem' },
              right: '0.2rem',
              zIndex: 99,
              fontSize: { xs: '10px', xl: '12px' },
            },
          }}
        >
          <LightTooltip
            title={
              <Typography variant='responsiveBody3' color={zeplinColor.Neutral1}>
                Filter
              </Typography>
            }
            placement='bottom-end'
            style={{ backgroundColor: zeplinColor.BasicBlack, borderRadius: '8px' }}
            // sx={{ [`& .${tooltipClasses.tooltip}`]: { backgroundColor: zeplinColor.Neutral1 } }}
          >
            <Box
              sx={{
                borderRadius: '50px',
                width: { xs: '28px', xl: '48px' },
                height: { xs: '28px', xl: '48px' },
                border: `1px solid ${zeplinColor.Neutral6}`,
                // p: '0px 10px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                position: 'relative',
                cursor: 'pointer',
              }}
              onClick={handleClick}
            >
              <img
                src={'/assets/filter.svg'}
                alt='filter-activity'
                id='filter-activity'
                className={style.icon}
              />
            </Box>
          </LightTooltip>
        </Badge>
        <CustomPopOver
          setAnchorEl={setAnchorEl}
          anchorEl={anchorEl}
          id={id}
          sx={{
            borderRadius: '14px',
            boxShadow: 'rgba(0, 0, 0, 0.15)',
            marginTop: '1rem',
            marginRight: '2rem',
            marginLeft: '-4rem',
          }}
        >
          <Box
            sx={{
              padding: '16px',
              width: { xs: '230px', xl: '283px' },
              height: '286px',
              display: 'flex',
              flexDirection: 'column',
              gap: '24px',
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant='responsiveHeading3' color={zeplinColor.BasicBlack}>
                Filter
              </Typography>
              <Typography
                variant='responsiveButtonSmall'
                color={
                  Object.values(selectedItems).every((val) => val.length <= 0)
                    ? zeplinColor.Neutral8
                    : zeplinColor.Brand1
                }
                onClick={
                  Object.values(selectedItems).every((val) => val.length <= 0)
                    ? () => {}
                    : handleClearAll
                }
                sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
              >
                Clear All
              </Typography>
            </Box>
            <FormGroup
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
              }}
            >
              <FormControl sx={{ width: '100%' }}>
                <InputLabel
                  id='demo-multiple-name-label'
                  sx={{
                    fontSize: { xs: '14px', xl: '16px' },
                    '&.Mui-focused': { color: zeplinColor.Neutral9 },
                  }}
                >
                  Tools
                </InputLabel>
                <Select
                  labelId='demo-multiple-name-label'
                  id='demo-multiple-name'
                  multiple
                  value={selectedItems.tools}
                  onChange={(e) => handleSelection(e, 'tools')}
                  input={
                    <OutlinedInput
                      sx={{
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderWidth: '1px',
                          color: zeplinColor.Neutral9,
                          borderColor: zeplinColor.Neutral9,
                        },
                        padding: '0px 14px',
                      }}
                      label='tools'
                    />
                  }
                  renderValue={(selected) => (selected as string[]).join(', ')}
                  MenuProps={MenuProps}
                  IconComponent={() => (
                    <img src='/assets/down-arrow.svg' className={style['icon-24']} />
                  )}
                >
                  {Tools.map((name) => (
                    <MenuItem
                      key={name}
                      value={name}
                      sx={{
                        fontSize: { xs: '14px', xl: '16px' }, // Match responsive font size
                        padding: { xs: '6px 16px', xl: '8px 16px' }, // Adjust padding for better appearance
                      }}
                    >
                      <Checkbox
                        checked={selectedItems.tools.indexOf(name) > -1}
                        sx={{
                          color: zeplinColor.BasicBlack,
                          '&.Mui-checked': {
                            color: zeplinColor.BasicBlack,
                          },
                        }}
                      />
                      {name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl sx={{ width: '100%' }}>
                <InputLabel
                  id='demo-multiple-name-label'
                  sx={{
                    fontSize: { xs: '14px', xl: '16px' },
                    '&.Mui-focused': { color: zeplinColor.Neutral9 },
                  }}
                >
                  Time range
                </InputLabel>
                <Select
                  labelId='demo-multiple-name-label'
                  id='demo-multiple-name'
                  // multiple
                  value={selectedItems.time}
                  onChange={(e) => handleSelection(e, 'time')}
                  input={
                    <OutlinedInput
                      sx={{
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderWidth: '1px',
                          color: zeplinColor.Neutral9,
                          borderColor: zeplinColor.Neutral9,
                        },
                        padding: '0px 14px',
                      }}
                      label='Time range'
                    />
                  }
                  IconComponent={() => (
                    <img src='/assets/down-arrow.svg' className={style['icon-24']} />
                  )}
                  renderValue={(selected) => selected[0]}
                  MenuProps={MenuProps}
                >
                  {TimeRange.map((name) => (
                    <MenuItem
                      key={name}
                      value={name}
                      sx={{
                        fontSize: { xs: '14px', xl: '16px' }, // Match responsive font size
                        padding: { xs: '6px 16px', xl: '8px 16px' }, // Adjust padding for better appearance
                      }}
                    >
                      <Radio
                        checked={selectedItems.time[0] === name} // Check if this radio should be selected
                        sx={{
                          color: zeplinColor.BasicBlack,
                          '&.Mui-checked': {
                            color: zeplinColor.BasicBlack,
                          },
                        }}
                      />
                      {name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box sx={{ display: 'flex', alignItems: 'flex-end', flex: 1 }}>
                <CustomButton
                  onClick={applyFilter}
                  variant='contained'
                  disabled={Object.values(selectedItems).every((val) => val.length <= 0)}
                  color={'primary'}
                  style={{
                    width: '100%',
                    borderRadius: '14px',
                    height: { xs: '48px', xl: '56px' },
                  }}
                  textStyle={{
                    color: Object.values(selectedItems).every((val) => val.length <= 0)
                      ? zeplinColor.Neutral8
                      : zeplinColor.Neutral1,
                    fontSize: { xs: '14px', xl: '16px' },
                  }}
                  text={'Apply'}
                />
              </Box>
            </FormGroup>
          </Box>
        </CustomPopOver>
      </Box>

      <Typography variant='responsiveHeading3'>All Activity</Typography>
      {newDataTotal + events?.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Chip
            label={
              <Typography variant='responsiveLabel1' color={zeplinColor.BasicBlack}>
                {newDataTotal + events?.length} new{' '}
                {newDataTotal + events?.length > 1 ? 'activities' : 'activity'}
              </Typography>
            }
            sx={{
              backgroundColor: zeplinColor.Neutral3,
              padding: '5px 12px 5px 12px',
              borderRadius: '18px',
            }}
          />
        </Box>
      )}

      {/* <Box sx={{ overflowY: 'auto', height: '50vh' }}>
       
        
      </Box> */}

      {todayLoading && todayData?.records?.length <= 0 ? (
        <>
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((val: any, ind: number) => (
            <CustomActivityTimeline
              val={val}
              key={ind}
              loading={true}
              newDataTotal={0}
              index={ind}
            />
          ))}
        </>
      ) : todayData?.records?.length <= 0 && slackMessage?.length <= 0 && !loading ? (
        <>
          {!IsSlackConnected && showSlackConnect && !location?.pathname?.includes('team') && (
            <CardStack
              height={128}
              extraHeightToshowmore={32}
              cards={[{ id: '', account: '', isSlack: true, sender: '', channel: '' }]}
              isLoginBtn={true}
              isTab={true}
              // maxHeight={messages.length < 3 ? '120px' : `${messages.length * 40 + 40}px`}
              renderCard={() => (
                <SlackMessageCard
                  message={''}
                  isExpanded={true}
                  newDataTotal={0}
                  index={0}
                  isLast={true}
                  isFirst={true}
                  total={0}
                  handleCardClick={() => {}}
                  isLoginBtn={true}
                  setShowSlackConnect={setShowSlackConnect}
                />
              )}
            />
          )}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              // // flex: 1,
              // height:
              //   screenHeight > 1200
              //     ? '60vh'
              //     : screenHeight > 1100
              //     ? '58vh'
              //     : screenHeight > 1000
              //     ? '54vh'
              //     : screenHeight > 900
              //     ? '50vh'
              //     : screenHeight > 800
              //     ? '45vh'
              //     : screenHeight > 700
              //     ? '38vh'
              //     : screenHeight > 600
              //     ? '25vh'
              //     : '10vh',
              height: 'calc(100vh - 400px)',
            }}
          >
            {' '}
            <img src={'/assets/no-activity.svg'} width={48} height={48} />
            <Typography
              variant='heading3'
              color={zeplinColor.BasicBlack}
              sx={{ marginTop: { xs: '20px', xl: '24px' } }}
            >
              No Activity Found
            </Typography>
            <Typography
              variant='body3'
              color={zeplinColor.Neutral8}
              sx={{ marginTop: { xs: '8px', xl: '12px' } }}
            >
              When there's something new, you'll see it here.
            </Typography>
          </Box>
        </>
      ) : (
        ''
      )}
      <InfiniteScrollColumn
        render={renderActivity}
        dataVal={todayData}
        next={async (next) => {
          try {
            // Load more Salesforce activities
            if (todayData?.totalFilteredRecords > todayData?.records?.length) {
              await getTodayActivity(next);
            }

            if (
              slackMessage.length &&
              IsSlackConnected &&
              !Object.values(channelLoading).some(Boolean)
            ) {
              if (location?.pathname?.includes('clients')) {
                if (channelID && channelCursors[channelID]) {
                  await getSlackMessage(channelID, channelCursors[channelID]!);
                }
              } else {
                const nextChannel = getNextChannelWithCursor();
                if (nextChannel) {
                  await getSlackMessage(nextChannel.channelId, nextChannel.cursor);
                }
              }
            }
          } catch (error) {
            console.error('Error loading more data:', error);
          }
        }}
        more={Object.values(channelCursors).some((cursor) => cursor !== null) ? 'true' : ''}
        extraData={IsSlackConnected ? slackMessage?.length : 1}
        loading={todayLoading}
        total={todayData?.totalFilteredRecords}
        children={children}
        // height={screenHeight > 640 ? '54vh' : screenHeight > 500 ? '41vh' : '39vh'}
        // height={'100vh'}
        loaderComponent={
          <>
            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((val: any, ind: number) => (
              <CustomActivityTimeline
                val={val}
                key={ind}
                loading={true}
                newDataTotal={0}
                index={ind}
              />
            ))}
          </>
        }
        height={'calc(100vh - 400px)'}
        paddingBottom='0rem'
      />

      {/* {todayLoading ? (
        <CustomLoader
          open={todayLoading}
          handleCloseModal={() => {
            setLoading(false);
          }}
        />
      ) : (
        ''
      )} */}
    </Box>
  );
};

export default ActivityTab;
